
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building, Users, Plus, UserPlus, Edit, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import UserRole from './UserRole';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from '@/context/AuthContext';

type EntrepriseUser = {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
};

type Organisation = {
  id: string;
  name: string;
  created_at: string;
  user_count: number;
  plan: string;
  owner_id: string;
};

const EntrepriseAccountSettings = () => {
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [orgUsers, setOrgUsers] = useState<EntrepriseUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    full_name: '',
    role: 'recruteur'
  });
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    plan: 'Enterprise'
  });
  const { user } = useAuth();

  useEffect(() => {
    loadOrganisations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadOrgUsers(selectedOrgId);
    }
  }, [selectedOrgId]);

  const loadOrganisations = async () => {
    try {
      setLoadingOrgs(true);
      
      // Récupérer les organisations depuis la base de données
      const { data, error } = await supabase
        .from('organizations')
        .select('*');
      
      if (error) throw error;
      
      // Pour chaque organisation, compter le nombre d'utilisateurs associés
      const orgsWithUserCount = await Promise.all(
        (data || []).map(async (org) => {
          // Compter les utilisateurs dans organization_members
          const { count, error: countError } = await supabase
            .from('organization_members')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', org.id);
          
          if (countError) {
            console.error('Erreur de comptage des membres:', countError);
            return {
              ...org,
              user_count: 0
            };
          }
          
          return {
            ...org,
            user_count: count || 0
          };
        })
      );
      
      setOrganisations(orgsWithUserCount);
      
      // Sélectionner la première organisation par défaut
      if (orgsWithUserCount.length > 0 && !selectedOrgId) {
        setSelectedOrgId(orgsWithUserCount[0].id);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des organisations:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les organisations.",
        variant: "destructive"
      });
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadOrgUsers = async (orgId: string) => {
    try {
      setLoading(true);
      
      // Chercher les utilisateurs qui appartiennent à cette organisation
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select('user_id, role')
        .eq('organization_id', orgId);
      
      if (membersError) throw membersError;
      
      // Récupérer les informations de profil pour chaque utilisateur
      const userProfiles = await Promise.all(
        (members || []).map(async (member) => {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', member.user_id)
            .single();
          
          if (profileError) {
            console.error('Erreur lors de la récupération du profil:', profileError);
            return null;
          }
          
          return {
            id: profile.id,
            full_name: profile.full_name || 'Utilisateur sans nom',
            email: profile.email,
            role: member.role,
            is_active: profile.is_active
          };
        })
      );
      
      // Filtrer les profils null (en cas d'erreur)
      setOrgUsers(userProfiles.filter(Boolean) as EntrepriseUser[]);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs de l'organisation.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = (orgId: string) => {
    setSelectedOrgId(orgId);
  };

  const handleCreateOrg = async () => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour créer une organisation.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Insérer une nouvelle organisation dans la base de données
      const { data, error } = await supabase
        .from('organizations')
        .insert({
          name: newOrgData.name,
          plan: newOrgData.plan,
          owner_id: user.id
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Rafraîchir la liste des organisations
      await loadOrganisations();
      
      // Sélectionner la nouvelle organisation
      if (data) {
        setSelectedOrgId(data.id);
      }
      
      // Réinitialiser le formulaire
      setNewOrgData({ name: '', plan: 'Enterprise' });
      setIsCreateOrgDialogOpen(false);
      
      toast({
        title: "Organisation créée",
        description: `L'organisation "${newOrgData.name}" a été créée avec succès.`
      });
    } catch (error: any) {
      console.error('Erreur lors de la création de l\'organisation:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer l'organisation.",
        variant: "destructive"
      });
    }
  };

  const handleAddUser = async () => {
    if (!selectedOrgId) return;
    
    try {
      // Vérifier si l'email existe déjà dans les profils
      const { data: existingUser, error: existingUserError } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', newUserData.email)
        .maybeSingle();
      
      if (existingUserError) throw existingUserError;
      
      let userId;
      
      if (!existingUser) {
        // L'utilisateur n'existe pas encore, on doit d'abord le créer
        toast({
          title: "Information",
          description: "Cet utilisateur n'existe pas encore. Il sera invité à rejoindre la plateforme."
        });
        
        // Ici, vous pourriez ajouter un code pour envoyer une invitation par email
        // Pour l'instant, nous allons juste afficher un message
        
        setNewUserData({
          email: '',
          full_name: '',
          role: 'recruteur'
        });
        
        setIsAddUserDialogOpen(false);
        return;
      } else {
        userId = existingUser.id;
      }
      
      // Ajouter l'utilisateur comme membre de l'organisation
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: selectedOrgId,
          user_id: userId,
          role: newUserData.role
        });
      
      if (memberError) throw memberError;
      
      // Mettre à jour corporate_group_id dans le profil de l'utilisateur
      const { error: updateProfileError } = await supabase
        .from('profiles')
        .update({ corporate_group_id: selectedOrgId })
        .eq('id', userId);
      
      if (updateProfileError) throw updateProfileError;
      
      // Rafraîchir la liste des utilisateurs
      await loadOrgUsers(selectedOrgId);
      
      // Rafraîchir aussi la liste des organisations pour mettre à jour user_count
      await loadOrganisations();
      
      // Réinitialiser le formulaire
      setNewUserData({
        email: '',
        full_name: '',
        role: 'recruteur'
      });
      
      setIsAddUserDialogOpen(false);
      
      toast({
        title: "Utilisateur ajouté",
        description: `${existingUser.email} a été ajouté à l'organisation.`
      });
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout de l\'utilisateur:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ajouter l'utilisateur à l'organisation.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedOrgId) return;
    
    try {
      // Supprimer l'utilisateur de l'organisation
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('organization_id', selectedOrgId)
        .eq('user_id', userId);
      
      if (error) throw error;
      
      // Mettre à jour corporate_group_id à null dans le profil
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ corporate_group_id: null })
        .eq('id', userId)
        .eq('corporate_group_id', selectedOrgId);
      
      if (updateError) throw updateError;
      
      // Rafraîchir la liste des utilisateurs
      await loadOrgUsers(selectedOrgId);
      
      // Rafraîchir aussi la liste des organisations pour mettre à jour user_count
      await loadOrganisations();
      
      toast({
        title: "Utilisateur supprimé",
        description: "L'utilisateur a été supprimé de l'organisation."
      });
    } catch (error: any) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'utilisateur de l'organisation.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Comptes entreprise</h3>
        
        <Dialog open={isCreateOrgDialogOpen} onOpenChange={setIsCreateOrgDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              <span>Nouvelle organisation</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une nouvelle organisation</DialogTitle>
              <DialogDescription>
                Créez une nouvelle organisation entreprise pour gérer plusieurs utilisateurs.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="org-name">Nom de l'organisation</Label>
                <Input 
                  id="org-name" 
                  value={newOrgData.name} 
                  onChange={(e) => setNewOrgData({...newOrgData, name: e.target.value})}
                  placeholder="Nom de l'entreprise" 
                />
              </div>
              
              <div>
                <Label htmlFor="org-plan">Plan</Label>
                <Select 
                  value={newOrgData.plan} 
                  onValueChange={(value) => setNewOrgData({...newOrgData, plan: value})}
                >
                  <SelectTrigger id="org-plan">
                    <SelectValue placeholder="Sélectionner un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                    <SelectItem value="Enterprise Plus">Enterprise Plus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOrgDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateOrg}>Créer l'organisation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Organisations</CardTitle>
            <CardDescription>
              Liste des comptes entreprise
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingOrgs ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {organisations.map(org => (
                  <div 
                    key={org.id} 
                    className={`p-3 rounded-md cursor-pointer flex justify-between hover:bg-slate-100 ${selectedOrgId === org.id ? 'bg-slate-100 border-l-4 border-blue-500' : ''}`}
                    onClick={() => handleSelectOrg(org.id)}
                  >
                    <div>
                      <p className="font-medium">{org.name}</p>
                      <p className="text-xs text-gray-500">Plan: {org.plan}</p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-1" />
                      {org.user_count}
                    </div>
                  </div>
                ))}
                
                {organisations.length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>Aucune organisation trouvée</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-lg">
                  {selectedOrgId 
                    ? `Utilisateurs - ${organisations.find(o => o.id === selectedOrgId)?.name}` 
                    : "Sélectionnez une organisation"}
                </CardTitle>
                <CardDescription>
                  Gérez les utilisateurs de cette organisation
                </CardDescription>
              </div>
              
              {selectedOrgId && (
                <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      <span>Ajouter un utilisateur</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un utilisateur</DialogTitle>
                      <DialogDescription>
                        Ajoutez un nouvel utilisateur à cette organisation.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="user-email">Email</Label>
                        <Input 
                          id="user-email" 
                          type="email" 
                          value={newUserData.email} 
                          onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                          placeholder="Email de l'utilisateur" 
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="user-role">Rôle</Label>
                        <Select 
                          value={newUserData.role} 
                          onValueChange={(value) => setNewUserData({...newUserData, role: value})}
                        >
                          <SelectTrigger id="user-role">
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="recruteur">Recruteur</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="evaluator">Évaluateur</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddUserDialogOpen(false)}>Annuler</Button>
                      <Button onClick={handleAddUser}>Ajouter l'utilisateur</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedOrgId && (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg mb-2">Aucune organisation sélectionnée</p>
                <p className="text-sm">Sélectionnez une organisation pour voir ses utilisateurs</p>
              </div>
            )}
            
            {selectedOrgId && loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
              </div>
            )}
            
            {selectedOrgId && !loading && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          Aucun utilisateur trouvé pour cette organisation
                        </TableCell>
                      </TableRow>
                    ) : (
                      orgUsers.map(user => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.full_name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell><UserRole role={user.role} /></TableCell>
                          <TableCell>
                            <Badge className={user.is_active ? "bg-green-500" : "bg-gray-500"}>
                              {user.is_active ? "Actif" : "Inactif"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button variant="ghost" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleRemoveUser(user.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EntrepriseAccountSettings;
